import React, { useEffect, useState, useContext } from "react";
import axios from "../utils/axiosConfig";
import { useParams, useLocation, useNavigation, useNavigate } from "react-router-dom";
import "../styles/RegistrationSummaryPage.css";
import { 
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Button, Box, Alert, Snackbar
} from "@mui/material";
import dayjs from "dayjs";
import { getCateringLabel, getOvernightLabel } from "../utils/labels";
import { useAuth } from "../context/AuthContext";
import SummarizeIcon from '@mui/icons-material/Summarize';
import BreadcrumbNavigation from "../components/BreadcrumbNavigation";

// Import ValidationContext
import { useValidation, ValidationContext } from '../context/ValidationContext';

import { handleApiError } from "../utils/apiErrorHandler";


import { useRegistrationStatus } from '../hooks/useRegistrationStatus';
import RegistrationStatusAlert from '../components/RegistrationStatusAlert';
import { toast } from 'react-toastify';

import { calculateFrontendPenaltyFees } from '../utils/judgesPenaltyCalculator';

//// Create a context consumer component that works with or without ValidationProvider
//const { refreshValidation } = useValidation();

interface SavedAthlete {
  _id: string;
  firstname: string;
  surname: string;
  age: number;
  gender: string;
  competitionGroup: string;
}

interface Athlete {
    _id: string;
    age: number;
    gender: string;
    firstname: string;
    surname: string;
}
  
interface AthleteRegistration {
    _id: string;
    competition: string;
    club: string;
    athlete: Athlete; // Athlet als verschachteltes Objekt
    competitionGroup?: string; // ID der Gruppe, wird später nachgeschlagen
    fee?: number;
}

interface SavedJudge {
  _id: string;
  firstname: string;
  surname: string;
  license: string;
  position: string[];
  type: string;
}
  
interface Judge {
    _id: string;
    firstname: string;
    surname: string;
    license: string;
}
  
interface JudgeRegistration {
    _id: string;
    competition: string;
    club: string;
    judge: Judge; // Kampfrichter als verschachteltes Objekt
    type: string;
    position: string[];
}
  
interface SavedOfficial {
  _id: string;
  firstname: string;
  surname: string;
  type: string;
  role: string;
}
  
interface Official {
  _id: string;
  firstname: string;
  surname: string;
}

interface OfficialRegistration {
  _id: string;
  competition: string;
  club: string;
  official: Official; // Offizieller als verschachteltes Objekt
  type: string;
  role: string;
}

interface CompetitionGroup {
    _id: string;
    name: string;
    type: string;
}
  
interface CompetitionData {
    _id: string;
    name: string;
    fees: { elite: number; basic: number };
    penaltyFees: { elite: number; basic: number };
    competitionGroups: { _id: string; name: string; type: string }[];
    judgeRules: { minAthletes: number; maxAthletes: number; requiredJudges: number; type: string }[];
    tShirt: { priceIndividualized: number; priceStandard: number };
    catering: Record<string, { price: number }>;
    overnight: Record<string, { price: number }>;
    registrationDeadline: Date;
    enableOfficial: boolean;
}

// New interface for saved registration data
interface SavedRegistrationData {
  _id: string;
  competition: string;
  club: string;
  athletes: AthleteRegistration[];
  judges: JudgeRegistration[];
  officials: OfficialRegistration[];
  tShirtBooking: TShirtBooking;
  cateringBooking: CateringBooking;
  overnightBooking: OvernightBooking;
  submissionDate: string;
  competitionData: CompetitionData;
}

interface TShirtBooking {
  individualizable: boolean;
  customText?: string;
  sizes: Record<string, number>;
}

interface CateringBooking {
  [key: string]: number;
}

interface OvernightBooking {
  [key: string]: number;
}

interface PreRegistration {
  _id: string;
  competition: string;
  club: string;
  tShirtBooking?: TShirtBooking;
  cateringBooking?: CateringBooking;
  overnightBooking?: OvernightBooking;
}

const RegistrationSummaryPage: React.FC = () => {
  const { competitionId, clubId: urlClubId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { clubId: authClubId } = useAuth();

  const validation = useContext(ValidationContext);
  const refreshValidation = validation?.refreshValidation || (() => Promise.resolve());
  
  // Determine if we're in view mode (showing saved registration)
  const isSavedViewMode = location.pathname.includes('/saved-summary');
  const isCurrentViewMode = location.pathname.includes('/current-summary');
  const isAdminView = isSavedViewMode || isCurrentViewMode;
    
  // Use URL clubId if provided, otherwise fall back to auth context
  const clubId = urlClubId || authClubId;

  const [competitionData, setCompetitionData] = useState<CompetitionData | null>(null);
  const [athletes, setAthletes] = useState<AthleteRegistration[]>([]);
  const [judges, setJudges] = useState<JudgeRegistration[]>([]);
  const [officials, setOfficials] = useState<OfficialRegistration[]>([]);
  const [competitionGroups, setCompetitionGroups] = useState<CompetitionGroup[]>([]);
  const [fees, setFees] = useState({ elite: 0, basic: 0 });
  const [lastSubmissionDate, setLastSubmissionDate] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(!isSavedViewMode);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [preRegistration, setPreRegistration] = useState<PreRegistration | null>(null);
  const [clubName, setClubName] = useState<string | null>(null);

  // State für Erfolgsmeldung
  const [submissionSuccess, setSubmissionSuccess] = useState<boolean>(false);

  // Prüfen, ob die Anmeldefrist abgelaufen ist
  const registrationStatus = useRegistrationStatus(competitionId);
  /*const isRegistrationClosed = React.useMemo(() => {
    if (!competitionData?.registrationDeadline) return false;
    
    const deadline = new Date(competitionData.registrationDeadline);
    // Optional: Einen Tag und 2 Stunden zur Deadline hinzufügen (wie in RegistrationProgress)
    deadline.setDate(deadline.getDate() + 1);
    
    const now = new Date();
    now.setHours(now.getHours() + 2);
    return deadline < now;
  }, [competitionData?.registrationDeadline]);*/

  // Funktionen zum Laden der Daten
  const loadRegistrationData = async () => {
    try {
      if(isAdminView) {
        const [
          athleteResponse,
          judgeResponse,
          officialResponse,
          submissionResponse,
          preRegistrationResponse
        ] = await Promise.all([
          axios.get(`/api/registrations/${competitionId}/${clubId}`),
          axios.get(`/api/judge-registrations/${competitionId}/${clubId}`),
          axios.get(`/api/official-registrations/${competitionId}/${clubId}`),
          axios.get(`/api/competition-registrations/${competitionId}/${clubId}/status`),
          axios.get(`/api/pre-registrations/${competitionId}/${clubId}`)
        ]);
        
        setAthletes(athleteResponse.data);
        setJudges(judgeResponse.data);
        setOfficials(officialResponse.data);
        setLastSubmissionDate(submissionResponse.data.lastSubmissionDate);
        setHasChanges(submissionResponse.data.hasChanges);
        setPreRegistration(preRegistrationResponse.data);
      } else {
        const [
          athleteResponse,
          judgeResponse,
          officialResponse,
          submissionResponse,
          preRegistrationResponse
        ] = await Promise.all([
          axios.get(`/api/registrations/${competitionId}/${clubId}`),
          axios.get(`/api/judge-registrations/${competitionId}`),
          axios.get(`/api/official-registrations/${competitionId}`),
          axios.get(`/api/competition-registrations/${competitionId}/${clubId}/status`),
          axios.get(`/api/pre-registrations/${competitionId}/${clubId}`)
        ]);
        
        setAthletes(athleteResponse.data);
        setJudges(judgeResponse.data);
        setOfficials(officialResponse.data);
        setLastSubmissionDate(submissionResponse.data.lastSubmissionDate);
        setHasChanges(submissionResponse.data.hasChanges);
        setPreRegistration(preRegistrationResponse.data);
      }
    } catch (error) {
      handleApiError(error, "Fehler beim Aktualisieren der Daten.");
    }
  };

  // Derived state
  const { penaltyFees, judgeRules } = competitionData || { penaltyFees: { elite: 0, basic: 0 }, judgeRules: [] };

  const availableTShirtSizes = preRegistration?.tShirtBooking?.sizes
    ? Object.entries(preRegistration.tShirtBooking.sizes)
        .filter(([_, quantity]) => quantity > 0) // Nur bestellte Größen anzeigen
        .map(([size, quantity]) => ({
          size,
          quantity,
          pricePerShirt: preRegistration?.tShirtBooking?.individualizable
            ? competitionData?.tShirt?.priceIndividualized || 0
            : competitionData?.tShirt?.priceStandard || 0,
        }))
    : [];

  const availableCateringOptions = competitionData?.catering
    ? Object.entries(competitionData.catering)
        .filter(([_, details]: [string, any]) => details.available) // Nur buchbare Optionen
        .map(([key, details]: [string, any]) => ({
          key,
          label: getCateringLabel(key), // Nutzt die Label-Funktion
          price: details.price,
        }))
    : [];

  const availableOvernightOptions = competitionData?.overnight
    ? Object.entries(competitionData.overnight)
        .filter(([_, details]: [string, any]) => details.available)
        .map(([key, details]: [string, any]) => ({
          key,
          label: getOvernightLabel(key),
          price: details.price,
        }))
    : [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        if (isSavedViewMode) {
          // Fetch saved registration data
          const savedDataResponse = await axios.get<SavedRegistrationData>(
            `/api/competition-registrations/${competitionId}/${clubId}/latest`
          );
          const competitionResponse = await axios.get(`/api/competitions/${competitionId}`);
          
          const savedData = savedDataResponse.data ;
          
          // Set all data from the saved registration
          setCompetitionData(competitionResponse.data);
          
          //Athleten normalisieren
          const savedAthletes = savedData.athletes as unknown as SavedAthlete[];
          const normalizedAthletes: AthleteRegistration[] = savedAthletes.map(athlete => ({
            _id: athlete._id,
            competition: competitionId as string, // Setze die competition ID aus dem Kontext
            club: clubId as string, // Setze die club ID aus dem Kontext
            athlete: {
              _id: athlete._id,
              firstname: athlete.firstname,
              surname: athlete.surname,
              age: athlete.age,
              gender: athlete.gender
            },
            competitionGroup: athlete.competitionGroup
          }));
          setAthletes(normalizedAthletes);
          //setAthletes(savedData.athletes);
          
          //Judges normalisieren
          const savedJudges = savedData.judges as unknown as SavedJudge[];
          const normalizedJudges: JudgeRegistration[] = savedJudges.map(judge => ({
            _id: judge._id,
            competition: competitionId as string, // Setze die competition ID aus dem Kontext
            club: clubId as string, // Setze die club ID aus dem Kontext
            judge: {
              _id: judge._id,
              firstname: judge.firstname,
              surname: judge.surname,
              license: judge.license,
            },
            position: judge.position,
            type: judge.type
          }));
          setJudges(normalizedJudges);          
          //setJudges(savedData.judges);

          //Officials normalisieren
          const savedOfficials = savedData.officials as unknown as SavedOfficial[];
          const normalizedOfficials: OfficialRegistration[] = savedOfficials.map(official => ({
            _id: official._id,
            competition: competitionId as string, // Setze die competition ID aus dem Kontext
            club: clubId as string, // Setze die club ID aus dem Kontext
            official: {
              _id: official._id,
              firstname: official.firstname,
              surname: official.surname,
            },
            role: official.role,
            type: official.type
          }));
          setOfficials(normalizedOfficials);
          //setOfficials(savedData.officials);
          setCompetitionGroups(competitionResponse.data.competitionGroups);
          setFees(competitionResponse.data.fees);
          setLastSubmissionDate(savedData.submissionDate);

          //normalize Preregistration
          let normalizedPreRegistration: PreRegistration | null = null;
          if (savedData.tShirtBooking || savedData.cateringBooking || savedData.overnightBooking) {
            normalizedPreRegistration = {
              _id: savedData._id || `temp-${Date.now()}`, // Falls keine ID vorhanden, generiere eine temporäre
              competition: String(competitionId),
              club: String(clubId),
              tShirtBooking: savedData.tShirtBooking,
              cateringBooking: savedData.cateringBooking,
              overnightBooking: savedData.overnightBooking
            };
          }
          setPreRegistration(normalizedPreRegistration);
          const clubNameData = await axios.get(`/api/clubs/${clubId}`);
          setClubName(clubNameData.data.name);
        } else {
          // Original code for fetching live registration data
          if(isAdminView) {
            const [
              athleteResponse,
              judgeResponse,
              officialResponse,
              competitionResponse,
              submissionResponse,
              preRegistrationResponse,
              clubResponse
            ] = await Promise.all([
              axios.get(`/api/registrations/${competitionId}/${clubId}`),
              axios.get(`/api/judge-registrations/${competitionId}/${clubId}`),
              axios.get(`/api/official-registrations/${competitionId}/${clubId}`),
              axios.get(`/api/competitions/${competitionId}`),
              axios.get(`/api/competition-registrations/${competitionId}/${clubId}/status`),
              axios.get(`/api/pre-registrations/${competitionId}/${clubId}`),
              axios.get(`/api/clubs/${clubId}`)
            ]);
            // Set data
            setCompetitionData(competitionResponse.data);
            setAthletes(athleteResponse.data);
            setJudges(judgeResponse.data);
            setOfficials(officialResponse.data);
            setCompetitionGroups(competitionResponse.data.competitionGroups);
            setFees(competitionResponse.data.fees);
            setLastSubmissionDate(submissionResponse.data.lastSubmissionDate);
            setHasChanges(submissionResponse.data.hasChanges);
            setPreRegistration(preRegistrationResponse.data);
            setClubName(clubResponse.data.name);
          } else {
            const [
              athleteResponse,
              judgeResponse,
              officialResponse,
              competitionResponse,
              submissionResponse,
              preRegistrationResponse
            ] = await Promise.all([
              axios.get(`/api/registrations/${competitionId}/${clubId}`),
              axios.get(`/api/judge-registrations/${competitionId}`),
              axios.get(`/api/official-registrations/${competitionId}`),
              axios.get(`/api/competitions/${competitionId}`),
              axios.get(`/api/competition-registrations/${competitionId}/${clubId}/status`),
              axios.get(`/api/pre-registrations/${competitionId}/${clubId}`)
            ]);
            // Set data
            setCompetitionData(competitionResponse.data);
            setAthletes(athleteResponse.data);
            setJudges(judgeResponse.data);
            setOfficials(officialResponse.data);
            setCompetitionGroups(competitionResponse.data.competitionGroups);
            setFees(competitionResponse.data.fees);
            setLastSubmissionDate(submissionResponse.data.lastSubmissionDate);
            setHasChanges(submissionResponse.data.hasChanges);
            setPreRegistration(preRegistrationResponse.data);
          }          
          
        }
      } catch (error) {
        handleApiError(error, "Fehler beim Laden der Daten.");
      } finally {
        setLoading(false);
      }
    };

    if (competitionId && clubId) {
      fetchData();
    }
  }, [competitionId, clubId, isAdminView]);

  const handleSubmit = async () => {
    try {      
      setSubmitting(true);
      await axios.post("/api/competition-registrations", { competition: competitionId, club: clubId });
      
      // Erfolgsmeldung anzeigen (z.B. mit einem temporären State)
      setSubmissionSuccess(true);
    
      // Daten neu laden anstatt zu navigieren
      const [
        submissionResponse,
        // Weitere API-Calls, die du benötigst
      ] = await Promise.all([
        axios.get(`/api/competition-registrations/${competitionId}/${clubId}/status`),
        // Weitere Promises hier
      ]);
      
      // Status aktualisieren
      setLastSubmissionDate(submissionResponse.data.lastSubmissionDate);
      setHasChanges(false); // Nach dem Absenden gibt es keine ungespeicherten Änderungen mehr
      
      // ValidationContext aktualisieren
      if(!isAdminView) {        
        refreshValidation();
      }
    } catch (error) {
      handleApiError(error, "Fehler beim Abschicken der Meldung.");
      //alert("Fehler beim Abschicken der Meldung.");
    } finally {
      setSubmitting(false);
      // Erfolgsbenachrichtigung nach einigen Sekunden ausblenden
      if (submissionSuccess) {
        setTimeout(() => setSubmissionSuccess(false), 5000);
      }
      if(!isAdminView) {        
        refreshValidation();
      }
    }
  };

  // Calculate total fees
  const totalFee = athletes.reduce((sum, athReg) => {
    const group = competitionGroups.find((g) => g._id === athReg.competitionGroup);
    if (!group) return sum; // Falls keine Gruppe gefunden, keine Gebühr berechnen
    
    const isElite = group.type === "Elite";
    return sum + (isElite ? fees.elite : fees.basic);
  }, 0);

  const totalFeeElite = athletes.reduce((sum, athReg) => {
    const group = competitionGroups.find((g) => g._id === athReg.competitionGroup);
    if (!group) return sum; // Falls keine Gruppe gefunden, keine Gebühr berechnen
    
    if (group.type === "Elite") {
      return sum + fees.elite;
    } 
    return sum;
  }, 0);

  const totalFeeBasis = athletes.reduce((sum, athReg) => {
    const group = competitionGroups.find((g) => g._id === athReg.competitionGroup);
    if (!group) return sum; // Falls keine Gruppe gefunden, keine Gebühr berechnen
    
    if (group.type === "Basis") {
      return sum + fees.basic;
    } 
    return sum;
  }, 0);
  

  ////// ALTER CODE FÜR STRAFGEBÜHREN-BERECHNUNG ///////
  /*
  // Penalty fees calculation
  const totalPenaltyFeeElite = (() => {
    if (!competitionData) return 0;
  
    const eliteAthletes = athletes.filter((ath) => {
      const group = competitionGroups.find((g) => g._id === ath.competitionGroup);
      return group?.type === "Elite";
    }).length;
  
    const eliteJudges = judges.filter((j) => j.type === "Elite").length;
    
    let penalty = 0;
    judgeRules.forEach((rule) => {
      if (rule.type === "Elite" && eliteAthletes >= rule.minAthletes && eliteAthletes <= rule.maxAthletes) {
        const missingJudges = Math.max(0, rule.requiredJudges - eliteJudges);        
        penalty += missingJudges * penaltyFees.elite * eliteAthletes;
      }
    });
  
    return penalty;
  })();
  
  const totalPenaltyFeeBasic = (() => {
    if (!competitionData) return 0;
  
    const basisAthletes = athletes.filter((ath) => {
      const group = competitionGroups.find((g) => g._id === ath.competitionGroup);
      return group?.type === "Basis";
    }).length;
  
    const basisJudges = judges.filter((j) => j.type === "Basis").length;
  
    let penalty = 0;
    judgeRules.forEach((rule) => {
      if (rule.type === "Basis" && basisAthletes >= rule.minAthletes && basisAthletes <= rule.maxAthletes) {
        const missingJudges = Math.max(0, rule.requiredJudges - basisJudges);
        penalty += missingJudges * penaltyFees.basic * basisAthletes;
      }
    });
  
    return penalty;
  })();
  */
  /////// ENDE ALTER CODE FÜR STRAFGEBÜHREN-BERECHNUNG ///////

  const penaltyResult = calculateFrontendPenaltyFees(
    athletes,
    judges,
    competitionGroups,
    judgeRules,
    penaltyFees,
    competitionData
  );

  const totalPenaltyFeeElite = penaltyResult.totalPenaltyFeeElite;
  const totalPenaltyFeeBasic = penaltyResult.totalPenaltyFeeBasic;
  //const totalPenaltyFee = penaltyResult.totalPenaltyFee;

  // Calculate T-Shirt fees
  const totalTShirtFee = preRegistration?.tShirtBooking
    ? Object.entries(preRegistration.tShirtBooking.sizes).reduce((sum, [size, quantity]) => {
        if (quantity > 0) {
          return sum + quantity * (preRegistration?.tShirtBooking?.individualizable
            ? competitionData?.tShirt.priceIndividualized || 0
            : competitionData?.tShirt.priceStandard || 0);
        }
        return sum;
      }, 0)
    : 0;

  // Calculate catering fees
  const totalCateringFee = preRegistration?.cateringBooking
    ? Object.entries(preRegistration.cateringBooking).reduce((sum, [key, quantity]) => {
        if (quantity > 0) {
          return sum + quantity * (competitionData?.catering[key]?.price || 0);
        }
        return sum;
      }, 0)
    : 0;

  // Calculate overnight fees
  const totalOvernightFee = preRegistration?.overnightBooking
    ? Object.entries(preRegistration.overnightBooking).reduce((sum, [key, quantity]) => {
        if (quantity > 0) {
          return sum + quantity * (competitionData?.overnight[key]?.price || 0);
        }
        return sum;
      }, 0)
    : 0;

  // Total penalty fee
  const totalPenaltyFee = totalPenaltyFeeElite + totalPenaltyFeeBasic;
  
  // Daten für die Breadcrumb-Navigation
  const breadcrumbData = {
    competitionName: competitionData?.name || '',
    viewMode: isSavedViewMode 
      ? (isCurrentViewMode ? "current" : "saved") 
      : "summary"
  };

  return (
    <Container component="main" maxWidth="md">
      {/* Erfolgsbenachrichtigung */}
      <Snackbar
        open={submissionSuccess}
        autoHideDuration={5000}
        onClose={() => setSubmissionSuccess(false)}
        message="Meldung wurde erfolgreich abgeschickt!"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Paper elevation={3} sx={{ padding: 3 }}>
        <Typography variant="h5" gutterBottom>
        {isCurrentViewMode 
          ? `Meldungsdetails ${clubName} (aktuell)`
          : isSavedViewMode 
            ? `Meldungsdetails ${clubName} (gesendet ${dayjs(lastSubmissionDate).format("DD.MM.YYYY HH:mm")})`
            : "Meldeübersicht"
        }
        </Typography>
  
        {loading ? (
          <Typography>Lade Daten...</Typography>
        ) : (
          <>
            {(["Elite", "Basis"] as const).map((type) => {
              const athletesFiltered = athletes.filter(
                (ath) =>
                  ath.competitionGroup &&
                  competitionGroups.some((g) => g._id === ath.competitionGroup && g.type === type)
              );
  
              const judgesFiltered = judges.filter((judgeReg) => judgeReg.type === type);
              const officialsFiltered = officials.filter((officialReg) => officialReg.type === type);
  
              // Zeige diesen Abschnitt nur, wenn mindestens ein Athlet gemeldet wurde
              if (athletesFiltered.length === 0) return null;
  
              return (
                <Box key={type} sx={{ mt: 3 }}>
                  <Typography variant="h6">{type}</Typography>
  
                  {/* Tabelle Sportler */}
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Sportler
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Vorname</TableCell>
                          <TableCell>Nachname</TableCell>
                          <TableCell>Jahrgang</TableCell>
                          <TableCell>Geschlecht</TableCell>
                          <TableCell>Wettkampfklasse</TableCell>
                          <TableCell>Meldegeld</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {athletesFiltered.map((athReg) => ( 
                          <TableRow key={athReg._id}>
                            <TableCell>{athReg.athlete.firstname}</TableCell>
                            <TableCell>{athReg.athlete.surname}</TableCell>
                            <TableCell>{athReg.athlete.age}</TableCell>
                            <TableCell>{athReg.athlete.gender}</TableCell>
                            <TableCell>
                              {competitionGroups.find((g) => g._id === athReg.competitionGroup)?.name || "-"}
                            </TableCell>
                            <TableCell>{type === "Elite" ? `${fees.elite.toFixed(2)} €` : `${fees.basic.toFixed(2)} €`}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                    Meldegeld {type}: {type === "Elite" ? `${totalFeeElite.toFixed(2)} €` : `${totalFeeBasis.toFixed(2)} €`}
                  </Typography>

                  {/* Kampfrichter */}
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Kampfrichter
                  </Typography>
                  {judgesFiltered.length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Lizenz</TableCell>
                            <TableCell>Wunschposition</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {judgesFiltered.map((judgeReg) => (
                            <TableRow key={judgeReg._id}>
                              <TableCell>{judgeReg.judge.firstname} {judgeReg.judge.surname}</TableCell>
                              <TableCell>{judgeReg.judge.license}</TableCell>
                              <TableCell>{judgeReg.position.join(", ")}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1 }}>Keine Kampfrichter gemeldet</Alert>
                  )}

                  <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                    Strafgebühr {type}: {type === "Elite" ? `${totalPenaltyFeeElite.toFixed(2)} €` : `${totalPenaltyFeeBasic.toFixed(2)} €`}
                  </Typography>
  
                  {/* Offizielle */}
                  {competitionData?.enableOfficial !== false && (
                    <>
                      <Typography variant="subtitle1" sx={{ mt: 2 }}>
                        Offizielle
                      </Typography>
                      {officialsFiltered.length > 0 ? (
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Rolle</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {officialsFiltered.map((officialReg) => (
                                <TableRow key={officialReg._id}>
                                  <TableCell>{officialReg.official.firstname} {officialReg.official.surname}</TableCell>
                                  <TableCell>{officialReg.role}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Alert severity="info" sx={{ mt: 1 }}>Keine Offiziellen gemeldet</Alert>
                      )}
                    </>
                  )}
                </Box>
              );
            })}

            {/* Zusatzbuchungen anzeigen, falls vorhanden */}
            {(preRegistration?.tShirtBooking || preRegistration?.cateringBooking || preRegistration?.overnightBooking) && (
              <>
                <Typography variant="h6" sx={{ mt: 4 }}>Zusätzliche Buchungen</Typography>

                {/* T-Shirts */}
                {availableTShirtSizes.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ mt: 3 }}>👕 T-Shirt-Bestellungen</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Größe</TableCell>
                          <TableCell>Preis pro Shirt</TableCell>
                          <TableCell>Anzahl</TableCell>
                          <TableCell>Gesamtpreis</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availableTShirtSizes.map(({ size, quantity, pricePerShirt }) => {
                          const totalPrice = quantity * pricePerShirt;
                          return (
                            <TableRow key={size}>
                              <TableCell>{size}</TableCell>
                              <TableCell>{pricePerShirt.toFixed(2)} €</TableCell>
                              <TableCell>{quantity}</TableCell>
                              <TableCell><strong>{totalPrice.toFixed(2)} €</strong></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Falls individualisiert, Individualisierungstext anzeigen */}
                  {preRegistration?.tShirtBooking?.individualizable && (
                    <Typography variant="subtitle1" sx={{ mt: 2 }}>
                      <strong>Individualisierung:</strong> {preRegistration.tShirtBooking.customText || "Keine Angabe"}
                    </Typography>
                  )}
                </>
              )}

                {/* Verpflegung */}
                {preRegistration?.cateringBooking && (
                <>
                  <Typography variant="h6" sx={{ mt: 3 }}>🍽️ Catering-Buchungen</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Option</TableCell>
                          <TableCell>Einzelpreis</TableCell>
                          <TableCell>Anzahl</TableCell>
                          <TableCell>Gesamtpreis</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availableCateringOptions.map(({ key, label, price }) => {
                          const quantity = preRegistration.cateringBooking?.[key as keyof typeof preRegistration.cateringBooking] ?? 0;
                          const totalPrice = quantity * price;

                          return quantity > 0 ? ( // Nur anzeigen, wenn tatsächlich etwas gebucht wurde
                            <TableRow key={key}>
                              <TableCell>{getCateringLabel(label)}</TableCell>
                              <TableCell>{price.toFixed(2)} €</TableCell>
                              <TableCell>{quantity}</TableCell>
                              <TableCell><strong>{totalPrice.toFixed(2)} €</strong></TableCell>
                            </TableRow>
                          ) : null;
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

                {/* Übernachtung */}
                {preRegistration?.overnightBooking && (
                <>
                  <Typography variant="h6" sx={{ mt: 3 }}>🌙 Übernachtung-Buchungen</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Option</TableCell>
                          <TableCell>Einzelpreis</TableCell>
                          <TableCell>Anzahl</TableCell>
                          <TableCell>Gesamtpreis</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {availableOvernightOptions.map(({ key, label, price }) => {
                          const quantity = preRegistration.overnightBooking?.[key as keyof typeof preRegistration.overnightBooking] ?? 0;
                          const totalPrice = quantity * price;

                          return quantity > 0 ? (
                            <TableRow key={key}>
                              <TableCell>{label}</TableCell>
                              <TableCell>{price.toFixed(2)} €</TableCell>
                              <TableCell>{quantity}</TableCell>
                              <TableCell><strong>{totalPrice.toFixed(2)} €</strong></TableCell>
                            </TableRow>
                          ) : null;
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
              </>
            )}

            {/* Gesamtkosten */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6">Gesamtkosten</Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic'}}>
                Meldegeld Elite: {totalFeeElite.toFixed(2)} € | Strafgebühr Elite: {totalPenaltyFeeElite.toFixed(2)} €
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic'}}>
                Meldegeld Basis: {totalFeeBasis.toFixed(2)} € | Strafgebühr Basis: {totalPenaltyFeeBasic.toFixed(2)} €
              </Typography>
              <Typography>Gesamtsumme Meldegeld: {(totalFee+totalPenaltyFee).toFixed(2)} €</Typography>
              {/* T-Shirt- & Verpflegungskosten anzeigen, falls vorhanden */}
              {totalTShirtFee > 0 && (
                <Typography variant="body2" sx={{ fontStyle: 'italic'}}>👕 T-Shirts: {totalTShirtFee.toFixed(2)} €</Typography>
              )}

              {totalCateringFee > 0 && (
                <Typography variant="body2" sx={{ fontStyle: 'italic'}}>🍽️ Verpflegung: {totalCateringFee.toFixed(2)} €</Typography>
              )}
              {totalOvernightFee > 0 && (
                <Typography variant="body2" sx={{ fontStyle: 'italic'}}>🌙 Übernachtung: {totalOvernightFee.toFixed(2)} €</Typography>
              )}
              <Typography variant="h6">Gesamtbetrag: {(totalFee + totalPenaltyFee + totalTShirtFee + totalCateringFee + totalOvernightFee).toFixed(2)} €</Typography>
            </Box>
  
            {/* Button zum Abschicken - nur im Live-Mode anzeigen */}
            {!isSavedViewMode && !isCurrentViewMode && (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Erfolgsnachricht, wenn gerade abgesendet wurde */}
                {submissionSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Meldung wurde erfolgreich abgeschickt!
                  </Alert>
                )}

                {/* Warnung, wenn Anmeldefrist abgelaufen */}
                {registrationStatus.isClosed && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Die Anmeldefrist ist abgelaufen. Es können keine Änderungen mehr vorgenommen werden.
                  </Alert>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={!hasChanges || submitting || registrationStatus.isClosed}
                >
                  {submitting 
                    ? "Meldung wird gesendet..." 
                    : registrationStatus.isClosed 
                      ? "Anmeldefrist abgelaufen" 
                      : "Meldung verschicken"
                  }
                </Button>
              </Box>
            )}
            
            {/* Anzeige des Meldedatums */}
            {lastSubmissionDate && (
              <Typography sx={{ mt: 2 }} color="text.secondary">
                Meldung {isSavedViewMode ? "erstellt" : "zuletzt versendet"} am: <strong>{dayjs(lastSubmissionDate).format("DD.MM.YYYY HH:mm")}</strong>
              </Typography>
            )}

              {!registrationStatus.isClosed && (
                <Typography sx={{ mt: 2, fontStyle: 'italic', fontSize :'80%' }} color="text.secondary" >
                  Hinweis: Meldungen können bis zum Meldeschluss ({dayjs(competitionData?.registrationDeadline).format("DD.MM.YYYY")}) noch verändert werden.<br />
                  Meldungen müssen nach jeder Änderung nochmals angeschickt werden, damit sie verarbeitet werden können. 
                </Typography>
              )}
          </>
        )}
        {/* Registration Status Alert */}
        <RegistrationStatusAlert competitionId={competitionId || ''} />
      </Paper>
    </Container>
  );
};

export default RegistrationSummaryPage;